import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

import * as userActions from '../../actions/userActions';
import UserService from '../../services/UserService';
import StorefrontService from '../../services/Storefront';
import Notifications from '../../services/Notifications';

import Loading from '../Loading';
import User from './User';
import Create from './Create';
import Details from './Details';
import ImportCustomers from './ImportCustomers';
import SubmitModal from '../shared/SubmitModal';

import { withStorefrontConfig } from '../../hooks/StorefrontSettingsContext';
import { withUserConfig } from '../../hooks/UserConfigContext';

const VIEW_COMPONENTS = {
    CREATE: Create,
    DETAIL: Details,
    IMPORT: ImportCustomers,
};

const Users = (props) => {
    const [currentView, setCurrentView] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedStorefront, setSelectedStorefront] = useState(props.currentUser.storefrontNumber);
    const [personas, setPersonas] = useState([]);
    const [users, setUsers] = useState(props.users);
    const [parsedCustomers, setParsedCustomers] = useState([]);
    const [updateUsersToImport, setUpdateUsersToImport] = useState([]);
    const [validFileName, setValidFileName] = useState(null);
    const [addOnly, setAddOnly] = useState(true);
    const [isImportProcessing, setIsImportProcessing] = useState(false);

    const mounted = useRef(true);
    const localizedStorefront = props.StorefrontConfig.labels;
    const localized = props.UserConfig.labels;
    const userSettings = props.UserConfig.settings;

    useEffect(() => {
        mounted.current = true;
        const fetchData = async () => {
            await props.userActions.GetFilteredUsers();
            await props.userActions.ReceivedCurrentUser(UserService.current);
            await fetchPersonaList();
            const fetchedUsers = await UserService.fetchFilteredUsers();
            setUsers(fetchedUsers);
        };
        fetchData();
        return () => { mounted.current = false; };
    }, []);

    const fetchPersonaList = async (storefrontNumber = selectedStorefront) => {
        const res = await StorefrontService.getPersonas(storefrontNumber);
        setPersonas(res);
    };

    const refreshFilteredUsers = async () => {
        await props.userActions.GetFilteredUsers();
        const updatedUsers = await UserService.fetchFilteredUsers();
        setUsers(updatedUsers);
    };

    const handleImportSave = async () => {
        if (updateUsersToImport.length === 0) {
            Notifications.error(localized.NoUsersSelected);
            return;
        }
        if (parsedCustomers.length === 0) {
            Notifications.error(localized.NoValidCustomers);
            return;
        }
        setIsImportProcessing(true);
        try {
            const response = await UserService.mergeCustomersFromCSV(parsedCustomers, updateUsersToImport, addOnly);
            setIsImportProcessing(false);
            if (!response) {
                Notifications.error(localized.ImportFailedMessage);
            } else {
                Notifications.message(localized.ImportSuccessMessage);
                setCurrentView(null);
            }
        } catch (error) {
            setIsImportProcessing(false);
            Notifications.error(localized.ImportErrorMessage, error);
        }
    };

    const handleUserSelect = (user) => {
        props.userActions.setSelectedUser(user);
        setSelectedUserId(user.id);
        setCurrentView('DETAIL');
    };

    const renderAddUserButton = () => (
        <div className="new-user" style={{ display: "flex" }}>
            <div className="add-import-button-wrapper">
                <button className="button" onClick={() => setCurrentView('CREATE')}>
                    + {localizedStorefront.ButtonText_AddUser}
                </button>
            </div>
            <div className="import-customers">
                <div className="add-import-button-wrapper" style={{ paddingLeft: "1rem" }}>
                    <button className="button" onClick={() => setCurrentView('IMPORT')}>
                        {localized.ImportCustomers}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderUserList = () => (
        props.users.map((user, key) => (
            <div key={key} className="action" onClick={() => handleUserSelect(user)}>
                <User user={user} isSelected={props.selectedUser && (props.selectedUser.id === user.id)} />
            </div>
        ))
    );

    const renderCurrentView = () => {
        if (!currentView) return null;
        const ViewComponent = VIEW_COMPONENTS[currentView];

        if (currentView === 'IMPORT') {
            return (
                <SubmitModal
                    isOpen={true}
                    onSubmit={isImportProcessing ? null : handleImportSave}
                    onClose={isImportProcessing ? null : () => setCurrentView(null)}
                    submitText={isImportProcessing ? <Loading /> : localized.Apply}
                    closeText={localized.Cancel}
                >
                    <ViewComponent
                        usersToImport={users}
                        currentUser={props.currentUser}
                        setUsersToImport={setUpdateUsersToImport}
                        updateUsersToImport={updateUsersToImport}
                        parsedCustomers={parsedCustomers}
                        addonly={addOnly}
                        setAddOnly={() => setAddOnly(!addOnly)}
                        setParsedCustomers={setParsedCustomers}
                        setValidFileName={setValidFileName}
                        validFileName={validFileName}
                    />
                </SubmitModal>
            );
        }

        if (currentView === 'DETAIL') {
            return (
                <ViewComponent
                    usersToClone={users}
                    currentUser={props.currentUser}
                    userId={selectedUserId}
                    personaList={personas}
                    setSelectedStorefront={setSelectedStorefront}
                    userSettings={userSettings}
                    refreshFilteredUsers={refreshFilteredUsers}
                />
            );
        }

        if (currentView === 'CREATE') {
            return (
                <ViewComponent
                    currentUser={props.currentUser}
                    isActive={true}
                    callback={(users) => {
                        if (users) props.userActions.receivedFilteredUsers(users);
                        setSelectedUserId(-1);
                        setCurrentView(null);
                    }}
                    customers={props.customers}
                    personaList={personas}
                />
            );
        }

        return null;
    };

    return (
        <React.Fragment>
            {props.users.length === 0 && <Loading type="brand" />}
            {props.users && (
                <div className="vertical-slide-wrapper">
                    <div className="users">
                        {renderAddUserButton()}
                        {renderUserList()}
                    </div>
                    <div className={`vertical-slide ${currentView ? 'open' : ''}`}>
                        <button className="fele-close" onClick={() => setCurrentView(null)} data-cy="close-user"></button>
                        {renderCurrentView()}
                    </div>
                </div>
            )}
            {(!props.users || props.users.length === 0) && (
                <div className="no-results">
                    <span className="section-header">{localizedStorefront.NoUsersMessage}</span>
                </div>
            )}
        </React.Fragment>
    );
};

Users.propTypes = {
    userActions: PropTypes.object,
    users: PropTypes.array,
    selectedUser: PropTypes.object,
    currentUser: PropTypes.object,
};

const mapStateToProps = (state) => ({
    users: state.user.filteredUsers,
    selectedUser: state.user.selectedUser,
    currentUser: state.user.currentUser,
    customers: state.user.customers,
});

const mapDispatchToProps = (dispatch) => ({
    userActions: bindActionCreators(userActions, dispatch),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStorefrontConfig(withUserConfig(Users)));
